// utils/scanToGuest.ts
import { NATIONALITIES_MAP, COUNTRIES_MAP } from "./guestUtils";
import { LovSource } from "../config/tenant";

type ScanField = {
  Read?: boolean;
  Validated?: boolean;
  RecommendedValue?: string | null;
  OCR?: { Read?: boolean; Validated?: boolean; Value?: string | null };
  MRZ?: { Read?: boolean; Validated?: boolean; Value?: string | null };
};

type ScanData = Record<string, ScanField>;

// ---- ScanDoc metadata shape ----
export type ScanMeta = {
  Country?: string;        // e.g. "HRV"
  DocumentSide?: string;   // e.g. "FRONT"
  DocumentType?: string;   // "ID" | "PASS" | ...
  DocumentSeries?: string; // e.g. "HRV-BO-04001"
};

// ----------------------------------------------------
// Helpers
// ----------------------------------------------------
function pickValue(field?: ScanField): string | undefined {
  const v = field?.RecommendedValue ?? field?.OCR?.Value ?? field?.MRZ?.Value;
  if (v == null) return undefined;
  const trimmed = String(v).trim();
  return trimmed.length ? trimmed : undefined;
}

/**
 * Parse "dd.MM.yyyy" (also tolerates dd/mm/yyyy, dd-mm-yyyy).
 *
 * Range-checks day (1..31), month (1..12) and year (1900..2100) so we never
 * forward physically impossible OCR garbage like `month=20` into the form
 * (real customer-reported case: passport scan emitted month=20, which then
 * shipped to Opera and was rejected at sync time).
 */
function parseDmy(dateStr: string) {
  const s = dateStr.replace(/[-/]/g, ".");
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (!m) return undefined;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (day < 1 || day > 31) return undefined;
  if (month < 1 || month > 12) return undefined;
  if (year < 1900 || year > 2100) return undefined;
  return { day, month, year };
}

/** Normalize 'M'|'F' -> 'male'|'female'. */
function normalizeGender(g?: string): "male" | "female" | undefined {
  if (!g) return undefined;
  const up = g.trim().toUpperCase();
  if (up === "F" || up === "FEMALE") return "female";
  if (up === "M" || up === "MALE") return "male";
  return undefined;
}

/**
 * Normalize and validate a scanned nationality value.
 *
 * The form's nationality dropdown is keyed by ISO-3 codes (NATIONALITIES_MAP).
 * Returning a value that isn't a known key (e.g. "GIANCARLO" — a real customer
 * case where the OCR/MRZ misaligned fields and put the given name into the
 * nationality slot) causes that bad value to be auto-filled and silently passed
 * through the form, only to be rejected by Opera at sync time with
 * INVALID_NATIONALITY. We must drop the value here so the dropdown stays empty
 * and the user is forced to pick a real nationality.
 */
function normalizeNationality(code?: string): string | undefined {
  if (!code) return undefined;
  const upper = code.trim().toUpperCase();
  return NATIONALITIES_MAP[upper] ? upper : undefined;
}

// ----------------------------------------------------
// Build ISO3 -> ISO2 using your existing maps
// ----------------------------------------------------
const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");

/** reverse index: normalized country name -> ISO2 */
const COUNTRY_NAME_TO_A2: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  Object.entries(COUNTRIES_MAP).forEach(([a2, name]) => {
    out[norm(name)] = a2;
  });
  return out;
})();

/** irregular nationality->country-name bridges */
const SPECIAL_NAME_BRIDGE: Record<string, string> = {
  "british": "great britain",
  "american": "united states",
  "russian": "russia",
  "czech": "czech republic",
  "slovak": "slovakia",
  "macedonian": "macedonia",
  "kosovar": "kosovo",
  "bosnian and herzegovinian": "bosnia herzegovina",
  "emirati": "united arab emirates",
  "stateless person": "", // no ISO2
};

/** heuristic mapping nationality adjective -> country name key used in COUNTRIES_MAP */
function nationalityToCountryKey(nat: string): string | undefined {
  const n = nat.toLowerCase().trim();
  if (SPECIAL_NAME_BRIDGE[n]) {
    const bridged = SPECIAL_NAME_BRIDGE[n];
    const key = norm(bridged);
    return key || undefined;
  }

  const endings = ["ian", "ean", "ese", "ish", "i", "ic", "er"];
  for (const end of endings) {
    if (n.endsWith(end)) {
      const base = n.slice(0, -end.length);
      const candidates = [
        base,
        base + "a",
        base + "ia",
        base + "land",
        base + "stan",
      ];
      for (const c of candidates) {
        const key = norm(c);
        if (COUNTRY_NAME_TO_A2[key]) return key;
      }
    }
  }

  const direct = norm(n.replace(/\bperson\b/g, "").trim());
  if (COUNTRY_NAME_TO_A2[direct]) return direct;

  return undefined;
}

/** small seed for very common cases; the rest inferred from maps */
const ISO3_TO_ISO2_BASE: Record<string, string> = {
  HRV: "HR", BIH: "BA", SRB: "RS", MNE: "ME", SVN: "SI", MKD: "MK",
  AUT: "AT", DEU: "DE", ITA: "IT", FRA: "FR", ESP: "ES", PRT: "PT", GRC: "GR",
  POL: "PL", CZE: "CZ", SVK: "SK", HUN: "HU", ROU: "RO", BGR: "BG",
  NLD: "NL", BEL: "BE", LUX: "LU", IRL: "IE", GBR: "GB",
  USA: "US", CAN: "CA", AUS: "AU", NZL: "NZ",
  SWE: "SE", NOR: "NO", DNK: "DK", FIN: "FI", ISL: "IS",
  UKR: "UA", RUS: "RU", TUR: "TR",
};

const ISO3_TO_ISO2: Record<string, string> = (() => {
  const out: Record<string, string> = { ...ISO3_TO_ISO2_BASE };
  for (const [a3, nat] of Object.entries(NATIONALITIES_MAP)) {
    const A3 = a3.toUpperCase();
    if (out[A3]) continue;
    const key = nationalityToCountryKey(nat);
    if (key) {
      const a2 = COUNTRY_NAME_TO_A2[key];
      if (a2) out[A3] = a2;
    }
  }
  return out;
})();

function iso3to2(a3?: string): string | undefined {
  if (!a3) return undefined;
  return ISO3_TO_ISO2[a3.trim().toUpperCase()];
}

function foldLatin(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

const COUNTRY_NAME_TO_A2_FOLDED: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  Object.entries(COUNTRIES_MAP).forEach(([a2, name]) => {
    out[foldLatin(name)] = a2;
  });
  return out;
})();

const COUNTRY_ALIASES_TO_A2: Record<string, string> = {
  hrvatska: "HR",
};

function normalizeAddressCountryCode(raw?: string): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if (!v) return undefined;

  const up = v.toUpperCase();
  if (up.length === 2 && COUNTRIES_MAP[up]) return up; // already ISO2

  if (up.length === 3) {
    const a2 = iso3to2(up);
    if (a2 && COUNTRIES_MAP[a2]) return a2;
  }

  const folded = foldLatin(v);
  if (!folded) return undefined;

  const byAlias = COUNTRY_ALIASES_TO_A2[folded];
  if (byAlias) return byAlias;

  return COUNTRY_NAME_TO_A2_FOLDED[folded];
}

// ----------------------------------------------------
// ID type mapping (domestic vs foreign) from metadata
// ----------------------------------------------------
function pickIdType(docType?: string, countryA3?: string): string | undefined {
  const dt = (docType ?? "").toUpperCase();
  const c3 = (countryA3 ?? "").toUpperCase();
  const isDomestic = c3 === "HRV" || c3 === "HR"; // treat HRV/HR as domestic

  if (dt === "ID")   return isDomestic ? "051" : "027"; // Osobna iskaznica (domaća/strana)
  if (dt === "PASS") return isDomestic ? "028" : "002"; // Osobna putovnica (domaća/strana)
  if (dt === "DL") return isDomestic ? "103" : "032"; // Vozačka dozvola (domaća/strana)
  return undefined;
}

/**
 * Optional per-field LOV source hints. When the chain has flipped
 * {@code addressCountry: NATIONALITY} (3-letter dropdown), the form's address-country select
 * expects 3-letter values and we must NOT collapse the scanned ISO-3 back to ISO-2 — that
 * would emit a code the dropdown can't represent and the user would land on an empty country.
 * Defaults to {@code COUNTRYCODES} for backwards compatibility with the pre-V14 behaviour.
 */
export type ScanLovSources = {
  addressCountry?: LovSource;
  birthCountry?: LovSource;
};

// ----------------------------------------------------
// Main builder
// ----------------------------------------------------
export function buildGuestPatchFromScan(
  data: ScanData,
  metadata?: ScanMeta[] | null,
  lovSources?: ScanLovSources
) {
  const firstName = pickValue(data.Name) ?? pickValue(data.GivenName);
  const lastName  = pickValue(data.Surname) ?? pickValue(data.FamilyName);

  const genderRaw = pickValue(data.Gender);
  const gender    = normalizeGender(genderRaw);

  const dobStr = pickValue(data.BirthDate);
  const dob    = dobStr ? parseDmy(dobStr) : undefined;

  const nationality = normalizeNationality(pickValue(data.Nationality));
  const idNumber    = pickValue(data.DocumentNumber);

  // Optional address fields from OCR
  const addressLine = pickValue(data.AddressStreet);
  const cityName    = pickValue(data.AddressCity);
  const postalCode  = pickValue(data.AddressZip);
  const addrCountryFromField = pickValue(data.AddressCountry); // likely ISO2 if present

  // NOTE: Do not set placeOfBirth (scanner returns city; form expects country)

  // Metadata (use first item)
  const meta = Array.isArray(metadata) ? metadata[0] : undefined;
  const metaDocType   = meta?.DocumentType?.toUpperCase();
  const metaCountryA3 = meta?.Country?.toUpperCase();  // e.g. "HRV"
  const metaCountryA2 = iso3to2(metaCountryA3);
  const idTypeFromMeta = pickIdType(metaDocType, metaCountryA3);

  // Build patch
  const patch: any = {};

  if (firstName)   patch.firstName    = firstName;
  if (lastName)    patch.lastName     = lastName;
  if (gender)      patch.gender       = gender;
  if (dob)         patch.dateOfBirth  = dob;
  if (nationality) patch.nationality  = nationality;

  // identification (idNumber + idType)
  if (idNumber || idTypeFromMeta) {
    patch.identificationInfo = {
      ...(patch.identificationInfo || {}),
      type: "Document",
      identification: {
        ...(patch.identificationInfo?.identification || {}),
        ...(idNumber ? { idNumber } : {}),
        ...(idTypeFromMeta ? { idType: idTypeFromMeta } : {}),
      },
    };
  }

  // addressInfo: prefer OCR country; else fallback to metadata Country.
  // When the chain's address-country dropdown is sourced from NATIONALITY, the form expects
  // 3-letter codes (passport MRZ format) and we forward them as-is. Otherwise we collapse
  // anything 3-letter into 2-letter to match the COUNTRYCODES dropdown.
  const addressCountryLov = lovSources?.addressCountry ?? "COUNTRYCODES";
  const finalCountry =
    addressCountryLov === "NATIONALITY"
      ? (addrCountryFromField?.trim().toUpperCase() || metaCountryA3 || undefined)
      : (normalizeAddressCountryCode(addrCountryFromField) ?? normalizeAddressCountryCode(metaCountryA2));

  const addr: any = {};
  if (addressLine) addr.addressLine = addressLine;
  if (cityName)    addr.cityName    = cityName;
  if (postalCode)  addr.postalCode  = postalCode;
  if (finalCountry) addr.country = finalCountry;

  if (Object.keys(addr).length) {
    patch.addressInfo = addr;
  }

  return patch;
}
